import { toast } from "@assetwatch/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { workClient } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";
import type { WorkOrderId } from "../lib/workIds";
import { procedureExecutionQueryRootKey } from "./useProcedureExecution";

export type BulkActionTarget = { id: WorkOrderId; number: string };

export type BulkActionFailure = BulkActionTarget & { reason: string };

export type BulkActionOutcome = {
  succeeded: BulkActionTarget[];
  failed: BulkActionFailure[];
};

type ItemResult =
  | { ok: true; target: BulkActionTarget }
  | { ok: false; target: BulkActionTarget; reason: string };

function toOutcome(results: ItemResult[]): BulkActionOutcome {
  return {
    succeeded: results
      .filter(
        (result): result is { ok: true; target: BulkActionTarget } => result.ok,
      )
      .map((result) => result.target),
    failed: results
      .filter(
        (
          result,
        ): result is { ok: false; target: BulkActionTarget; reason: string } =>
          !result.ok,
      )
      .map((result) => ({ ...result.target, reason: result.reason })),
  };
}

async function readErrorMessage(response: Response, fallback: string) {
  const body = (await response.json().catch(() => null)) as {
    error?: string;
  } | null;
  return body?.error ?? fallback;
}

function pluralize(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

function announceOutcome({
  outcome,
  verbPast,
  verbInfinitive,
}: {
  outcome: BulkActionOutcome;
  verbPast: string;
  verbInfinitive: string;
}) {
  const successCount = outcome.succeeded.length;
  const failureCount = outcome.failed.length;
  if (failureCount === 0) {
    toast.add(`${pluralize(successCount, "Work Order")} ${verbPast}.`, {
      icon: true,
    });
    return;
  }
  if (successCount === 0) {
    toast.error(
      `Unable to ${verbInfinitive} the selected Work Orders. See the results for details.`,
      { icon: true },
    );
    return;
  }
  toast.add(
    `${pluralize(successCount, "Work Order")} ${verbPast}. ${pluralize(failureCount, "Work Order")} could not be updated.`,
    { icon: true },
  );
}

async function invalidateWorkOrderQueries(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: procedureExecutionQueryRootKey }),
    queryClient.invalidateQueries({ queryKey: ["work-orders"] }),
    queryClient.invalidateQueries({ queryKey: ["work-order-detail"] }),
    queryClient.invalidateQueries({ queryKey: ["work-order-assignments"] }),
  ]);
}

export function useBulkCompleteWorkOrdersMutation() {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      targets,
      completionNotes,
    }: {
      targets: BulkActionTarget[];
      completionNotes: string;
    }): Promise<BulkActionOutcome> => {
      if (!selectedCustomer || !selectedFacility) {
        throw new Error("A customer and facility must be selected.");
      }
      const results = await Promise.all(
        targets.map(async (target): Promise<ItemResult> => {
          try {
            const response = await workClient.v1.customers[
              ":customerId"
            ].facilities[":facilityId"]["work-orders"][
              ":workOrderId"
            ].complete.$post({
              param: {
                customerId: selectedCustomer.id,
                facilityId: selectedFacility.id,
                workOrderId: String(target.id),
              },
              json: { completionNotes },
            });
            if (!response.ok) {
              return {
                ok: false,
                target,
                reason: await readErrorMessage(
                  response,
                  "Unable to complete Work Order.",
                ),
              };
            }
            return { ok: true, target };
          } catch {
            return {
              ok: false,
              target,
              reason: "Unable to complete Work Order.",
            };
          }
        }),
      );
      return toOutcome(results);
    },
    onSuccess: async (outcome) => {
      await invalidateWorkOrderQueries(queryClient);
      announceOutcome({
        outcome,
        verbPast: "marked as done",
        verbInfinitive: "mark",
      });
    },
    onError: () =>
      toast.error("Unable to complete the selected Work Orders. Try again.", {
        icon: true,
      }),
  });
}

export function useBulkCancelWorkOrdersMutation() {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      targets,
      cancellationReason,
    }: {
      targets: BulkActionTarget[];
      cancellationReason: string;
    }): Promise<BulkActionOutcome> => {
      if (!selectedCustomer || !selectedFacility) {
        throw new Error("A customer and facility must be selected.");
      }
      const results = await Promise.all(
        targets.map(async (target): Promise<ItemResult> => {
          try {
            const response = await workClient.v1.customers[
              ":customerId"
            ].facilities[":facilityId"]["work-orders"][
              ":workOrderId"
            ].cancel.$post({
              param: {
                customerId: selectedCustomer.id,
                facilityId: selectedFacility.id,
                workOrderId: String(target.id),
              },
              json: { cancellationReason },
            });
            if (!response.ok) {
              return {
                ok: false,
                target,
                reason: await readErrorMessage(
                  response,
                  "Unable to cancel Work Order.",
                ),
              };
            }
            return { ok: true, target };
          } catch {
            return {
              ok: false,
              target,
              reason: "Unable to cancel Work Order.",
            };
          }
        }),
      );
      return toOutcome(results);
    },
    onSuccess: async (outcome) => {
      await invalidateWorkOrderQueries(queryClient);
      announceOutcome({
        outcome,
        verbPast: "canceled",
        verbInfinitive: "cancel",
      });
    },
    onError: () =>
      toast.error("Unable to cancel the selected Work Orders. Try again.", {
        icon: true,
      }),
  });
}

export function useBulkAssignToMeMutation() {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      targets,
      userId,
    }: {
      targets: BulkActionTarget[];
      userId: number;
    }): Promise<BulkActionOutcome> => {
      if (!selectedCustomer || !selectedFacility) {
        throw new Error("A customer and facility must be selected.");
      }
      const results = await Promise.all(
        targets.map(async (target): Promise<ItemResult> => {
          try {
            const response = await workClient.v1.customers[
              ":customerId"
            ].facilities[":facilityId"]["work-orders"][
              ":workOrderId"
            ].assignments.$post({
              param: {
                customerId: selectedCustomer.id,
                facilityId: selectedFacility.id,
                workOrderId: String(target.id),
              },
              json: { userId },
            });
            if (!response.ok) {
              return {
                ok: false,
                target,
                reason: await readErrorMessage(
                  response,
                  "Unable to assign Work Order.",
                ),
              };
            }
            return { ok: true, target };
          } catch {
            return {
              ok: false,
              target,
              reason: "Unable to assign Work Order.",
            };
          }
        }),
      );
      return toOutcome(results);
    },
    onSuccess: async (outcome) => {
      await invalidateWorkOrderQueries(queryClient);
      announceOutcome({
        outcome,
        verbPast: "assigned to you",
        verbInfinitive: "assign",
      });
    },
    onError: () =>
      toast.error("Unable to assign the selected Work Orders. Try again.", {
        icon: true,
      }),
  });
}
